import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchOrders, formatCents, type PrintOrder } from '../lib/orders-api';

const TERMINAL = new Set(['shipped', 'canceled', 'error']);
const POLL_INTERVAL_MS = 30_000;

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; className: string }> = {
    pending_payment: { label: 'Pending Payment', className: 'bg-stone-100 text-stone-600' },
    paid:            { label: 'Paid',             className: 'bg-stone-100 text-stone-600' },
    created:         { label: 'Created',           className: 'bg-stone-100 text-stone-600' },
    in_production:   { label: 'In Production',     className: 'bg-amber-100 text-amber-700' },
    shipped:         { label: 'Shipped',            className: 'bg-green-100 text-green-700' },
    canceled:        { label: 'Canceled',           className: 'bg-red-100 text-red-700' },
    error:           { label: 'Error',              className: 'bg-red-100 text-red-700' },
  };
  const badge = map[status] ?? { label: status, className: 'bg-stone-100 text-stone-500' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
}

function OrderCard({ order }: { order: PrintOrder }) {
  const date = new Date(order.createdAt).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-stone-500">{date}</p>
          {order.bookTitle && (
            <p className="text-stone-900 font-semibold mt-0.5">{order.bookTitle}</p>
          )}
        </div>
        <StatusBadge status={order.status} />
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {order.bindingType && (
          <>
            <dt className="text-stone-500">Binding</dt>
            <dd className="text-stone-900 capitalize">{order.bindingType}</dd>
          </>
        )}
        {order.breakdown && (
          <>
            <dt className="text-stone-500">Total paid</dt>
            <dd className="text-stone-900 font-medium">{formatCents(order.breakdown.totalCents)}</dd>
          </>
        )}
        <dt className="text-stone-500">Order ID</dt>
        <dd className="text-stone-600 font-mono text-xs">{order.orderId.slice(-8).toUpperCase()}</dd>
      </dl>

      {order.trackingUrl && (
        <a
          href={order.trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700 font-medium"
        >
          Track shipment
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
            <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h4a.75.75 0 010 1.5h-4z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
          </svg>
        </a>
      )}
    </div>
  );
}

export function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<PrintOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const ordersRef = useRef<PrintOrder[]>([]);

  const load = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const data = await fetchOrders();
      if (!mountedRef.current) return;
      setOrders(data);
      ordersRef.current = data;
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load orders');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    load(true);

    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (ordersRef.current.length > 0 && ordersRef.current.every(o => TERMINAL.has(o.status))) return;
      load();
    }, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <main className="min-h-dvh bg-stone-50 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-serif text-stone-900">My printed books</h1>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => load(false)}
                className="text-sm text-stone-500 hover:text-amber-600 transition-colors"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={() => navigate('/book')}
                className="text-sm text-stone-500 hover:text-amber-600 transition-colors"
              >
                ← Back to Book
              </button>
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Loading spinner */}
        {loading && (
          <div className="flex justify-center py-12">
            <svg className="animate-spin h-8 w-8 text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-label="Loading">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        )}

        {/* Empty state */}
        {!loading && orders.length === 0 && !error && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 flex flex-col items-center text-center space-y-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12 text-stone-300" aria-hidden="true">
              <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
            </svg>
            <p className="text-stone-600">No printed books yet.</p>
            <button
              type="button"
              onClick={() => navigate('/book')}
              className="px-5 py-2.5 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors"
            >
              Order My Book
            </button>
          </div>
        )}

        {/* Order cards */}
        {!loading && orders.map(order => (
          <OrderCard key={order.orderId} order={order} />
        ))}
      </div>
    </main>
  );
}
